import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'
import { useState } from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormSetError,
} from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import { useCartStore } from '@/stores/cart/cartStore'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useSearchStore } from '@/stores/feed/searchStore'
import { publicApi, setMemoryToken } from '@/utils/api/api'
import { ensureWebAccess, WebAccessDeniedError } from '@/utils/auth/webAccess'
import { denormalizePhoneForBackend } from '@/utils/phone/denormalizePhoneForBackend'

import cn from '../Forms.module.scss'
import { PhoneInput } from '../PhoneInput/PhoneInput'

type LoginFormValues = {
  phone: string
  password: string
}

type ByPasswordFormPropsType = {
  onForgotPassword: () => void
  control: Control<LoginFormValues>
  errors: FieldErrors<LoginFormValues>
  handleSubmit: UseFormHandleSubmit<LoginFormValues>
  setError: UseFormSetError<LoginFormValues>
  getValues: UseFormGetValues<LoginFormValues>
}

function ByPasswordForm({
  onForgotPassword,
  control,
  errors,
  handleSubmit,
  setError,
}: ByPasswordFormPropsType) {
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const navigate = useNavigate()

  const onSubmit = async (data: LoginFormValues) => {
    const phoneDigits = denormalizePhoneForBackend(data.phone)
    toast.promise(
      publicApi
        .post(
          '/auth/access-token',
          { login: phoneDigits, password: data.password },
          { _skipAuth: true }
        )
        .then(ensureWebAccess),
      {
        loading: 'Вход...',
        success: (response) => {
          localStorage.removeItem('currentClientId')
          localStorage.removeItem('selectedOrganisationCode')
          localStorage.removeItem('cart-storage')
          useSearchStore.getState().setInitialSearchStore()
          useFeedStore.getState().setInitialFeedStore()
          useCartStore.getState().setInitialCartStore()

          const { accessToken, refreshToken } = response.data.token
          setMemoryToken(accessToken)
          localStorage.setItem('refresh-token', refreshToken)
          navigate('/', { replace: true })
          return 'Вход выполнен'
        },
        error: (error) => {
          const errorMessage =
            error instanceof WebAccessDeniedError
              ? error.message
              : error.response?.data?.message || 'Ошибка входа'
          if (errorMessage.toLowerCase().includes('пароль')) {
            setError('password', { type: 'manual', message: errorMessage })
          } else {
            setError('phone', { type: 'manual', message: errorMessage })
          }
          return errorMessage
        },
      }
    )
  }

  return (
    <div className={cn.formBlock}>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="on">
        <div className={cn.inputs}>
          <div className={cn.inputWrapper}>
            <label className={cn.label} htmlFor="login-phone">
              Телефон
            </label>
            <PhoneInput
              id="login-phone"
              name="phone"
              control={control}
              errors={errors}
              autoComplete="username"
            />
            {errors.phone && (
              <p className={cn.errorMessage}>{errors.phone.message}</p>
            )}
          </div>

          <div className={cn.inputWrapper}>
            <div className={cn.labelRow}>
              <label className={cn.label} htmlFor="login-password">
                Пароль
              </label>
              <button
                type="button"
                className={cn.forgotPasswordLink}
                onClick={onForgotPassword}
              >
                Забыли пароль?
              </button>
            </div>
            <div className={cn.inputWithEye}>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Пароль"
                    autoComplete="current-password"
                    className={[
                      cn.input,
                      errors.password ? cn.inputInvalid : cn.inputValid,
                    ].join(' ')}
                  />
                )}
              />
              <button
                type="button"
                className={cn.eyeOutlinedContainer}
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </button>
            </div>
            {errors.password && (
              <p className={cn.errorMessage}>{errors.password.message}</p>
            )}
          </div>
        </div>

        <AtomButton fullWidth htmlType="submit">
          Войти
        </AtomButton>
      </form>
    </div>
  )
}

export default ByPasswordForm
